<template>
  <div
    ref="itemEl"
    :class="[
      'flex-1 relative overflow-hidden text-center shadow-[0_-1px_0_0_#fff]',
      'lg:border-r-2 border-white last:border-0',
    ]"
  >
    <a
      :class="[
        'flex items-center justify-center h-full',
        'relative cursor-pointer p-2',
        'text-white lg:text-lg text-md hover:text-secondary focus:text-white focus-visible:text-[#0b0b0b]',
        'no-underline font-semibold uppercase',
      ]"
      @click="props.action"
      @mouseenter="handleMouseEnter"
      @mouseleave="handleMouseLeave"
    >
      {{ t(props.label) }}
    </a>

    <div
      ref="marqueeEl"
      class="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none bg-secondary translate-y-[101%]"
    >
      <div
        ref="marqueeInnerEl"
        :class="['h-full flex', props.enableMarquee ? 'w-[200%]' : 'w-full']"
      >
        <div
          :class="[
            'gap-8',
            'flex items-center justify-center',
            'relative size-full will-change-transform',
            props.enableMarquee && 'animate_marquee',
          ]"
        >
          <template v-for="i in slidersCount" :key="i">
            <span
              :class="[
                'text-white text-center uppercase font-semibold lg:text-lg text-md text-nowrap',
                'mx-2',
              ]"
            >
              {{ t(props.label) }}
            </span>
            <NuxtImg
              v-if="props.image"
              :class="['p-[1em_0] bg-cover bg-center']"
              :src="props.image"
            />
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { gsap } from 'gsap'
  import type { MenuItemProps } from '~/types/common/item'

  const props = withDefaults(defineProps<MenuItemProps>(), {
    label: '',
    action: undefined,
    enableMarquee: true,
    image: '',
  })

  const { t } = useI18n()

  const itemEl = useTemplateRef('itemEl')
  const marqueeEl = useTemplateRef('marqueeEl')
  const marqueeInnerEl = useTemplateRef('marqueeInnerEl')

  /** 按钮悬停后内部用于轮播的子组件循环数量 */
  const slidersCount = props.enableMarquee ? 40 : 1

  const animationDefaults = { duration: 0.6, ease: 'expo' }

  const findClosestEdge = (
    mouseX: number,
    mouseY: number,
    width: number,
    height: number,
  ): 'top' | 'bottom' => {
    const topEdgeDist = Math.pow(mouseX - width / 2, 2) + Math.pow(mouseY, 2)
    const bottomEdgeDist = Math.pow(mouseX - width / 2, 2) + Math.pow(mouseY - height, 2)
    return topEdgeDist < bottomEdgeDist ? 'top' : 'bottom'
  }

  // 直接从本地 ref 的 value 中取值
  const handleMouseEnter = (ev: MouseEvent) => {
    const elItem = itemEl.value
    const elMarquee = marqueeEl.value
    const elMarqueeInner = marqueeInnerEl.value

    if (!elItem || !elMarquee || !elMarqueeInner) return

    const rect = elItem.getBoundingClientRect()
    const edge = findClosestEdge(
      ev.clientX - rect.left,
      ev.clientY - rect.top,
      rect.width,
      rect.height,
    )

    const tl = gsap.timeline({ defaults: animationDefaults })
    tl.set(elMarquee, { y: edge === 'top' ? '-101%' : '101%' })
      .set(elMarqueeInner, { y: edge === 'top' ? '101%' : '-101%' })
      .to([elMarquee, elMarqueeInner], { y: '0%' })
  }

  const handleMouseLeave = (ev: MouseEvent) => {
    const elItem = itemEl.value
    const elMarquee = marqueeEl.value
    const elMarqueeInner = marqueeInnerEl.value

    if (!elItem || !elMarquee || !elMarqueeInner) return

    const rect = elItem.getBoundingClientRect()
    const edge = findClosestEdge(
      ev.clientX - rect.left,
      ev.clientY - rect.top,
      rect.width,
      rect.height,
    )

    const tl = gsap.timeline({ defaults: animationDefaults })
    tl.to(elMarquee, { y: edge === 'top' ? '-101%' : '101%' }).to(
      elMarqueeInner,
      { y: edge === 'top' ? '101%' : '-101%' },
      // 使用 '<' 让两个动画同时开始
      '<',
    )
  }
</script>

<style scoped>
  .animate_marquee {
    animation: marquee 20s linear infinite;
  }
  /* 滚动动画 */
  @keyframes marquee {
    from {
      transform: translateX(0%);
    }
    to {
      transform: translateX(-50%);
    }
  }
</style>
